/**
 * RouteStylesDisplay Component
 *
 * Displays route styles organized by category (Styles, Holds, Skills)
 * with icons for each style type.
 */

import { useMemo, type CSSProperties } from 'react';
import {
  type RouteStyle,
  HOLD_STYLES,
  CLIMBING_STYLES,
  SKILL_STYLES,
  HOLD_STYLE_INFO,
  CLIMBING_STYLE_INFO,
  SKILL_STYLE_INFO,
} from '@flash/shared';

/**
 * Props for RouteStylesDisplay component
 */
export interface RouteStylesDisplayProps {
  /** Array of route styles to display */
  styles: RouteStyle[];
}

/**
 * Get display info for a style
 */
const getStyleInfo = (style: RouteStyle) => {
  const allInfo = [...HOLD_STYLE_INFO, ...CLIMBING_STYLE_INFO, ...SKILL_STYLE_INFO];
  return allInfo.find((info) => info.id === style);
};

/**
 * Categorize styles into their respective groups
 */
const categorizeStyles = (styles: RouteStyle[]) => {
  const holdStyles = styles.filter((s) => (HOLD_STYLES as readonly string[]).includes(s));
  const climbingStyles = styles.filter((s) => (CLIMBING_STYLES as readonly string[]).includes(s));
  const skillStyles = styles.filter((s) => (SKILL_STYLES as readonly string[]).includes(s));

  return { holdStyles, climbingStyles, skillStyles };
};

/**
 * StyleChip - Individual style badge
 */
const StyleChip = ({ style }: { style: RouteStyle }) => {
  const info = getStyleInfo(style);
  const label = info?.label || style;

  return (
    <div style={chipStyle} title={info?.description}>
      <span style={chipLabelStyle}>{label}</span>
    </div>
  );
};

/**
 * StyleCategory - A category section with title and chips
 */
const StyleCategory = ({ title, styles }: { title: string; styles: RouteStyle[] }) => {
  if (styles.length === 0) return null;

  return (
    <div style={categoryStyle}>
      <div style={categoryTitleStyle}>{title}</div>
      <div style={chipsContainerStyle}>
        {styles.map((style) => (
          <StyleChip key={style} style={style} />
        ))}
      </div>
    </div>
  );
};

/**
 * RouteStylesDisplay - Displays route styles organized by category
 */
export const RouteStylesDisplay = ({ styles }: RouteStylesDisplayProps) => {
  const { holdStyles, climbingStyles, skillStyles } = useMemo(
    () => categorizeStyles(styles),
    [styles]
  );

  // Don't render if no styles
  if (styles.length === 0) {
    return null;
  }

  return (
    <div style={containerStyle}>
      <StyleCategory title="Styles" styles={climbingStyles} />
      <StyleCategory title="Holds" styles={holdStyles} />
      <StyleCategory title="Skills" styles={skillStyles} />
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const categoryStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const categoryTitleStyle: CSSProperties = {
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: 'var(--color-text-primary, #1f2937)',
};

const chipsContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
};

const chipStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  borderRadius: '8px',
  border: '1px solid var(--color-border, #e5e7eb)',
};

const chipLabelStyle: CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--color-text-primary, #1f2937)',
};

export default RouteStylesDisplay;
